import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Typography, Button, IconButton, TextField, Paper,
  Tabs, Tab, Switch, FormControlLabel, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Chip, Tooltip, Alert, Snackbar, Collapse,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import api from "../../utils/api";

// ─── helpers ─────────────────────────────────────────────────────────────────

function buildTree(flat) {
  const map = {};
  flat.forEach(n => { map[n.id] = { ...n, children: [] }; });
  const roots = [];
  flat.forEach(n => {
    if (n.parent_id == null) roots.push(map[n.id]);
    else if (map[n.parent_id]) map[n.parent_id].children.push(map[n.id]);
  });
  roots.sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name));
  const sortChildren = node => {
    node.children.sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name));
    node.children.forEach(sortChildren);
  };
  roots.forEach(sortChildren);
  return roots;
}

// ─── Single tree node ─────────────────────────────────────────────────────────

function CategoryNode({ node, depth, onAdd, onEdit, onDelete, expandedIds, toggleExpand }) {
  const hasChildren = node.children && node.children.length > 0;
  const isExpanded = expandedIds.has(node.id);

  return (
    <Box>
      <Box
        sx={{
          display: "flex", alignItems: "center", gap: 0.5,
          pl: depth * 3,
          py: 0.5,
          borderRadius: 1,
          "&:hover": { bgcolor: "rgba(47,99,173,0.04)" },
        }}
      >
        <IconButton size="small" sx={{ visibility: hasChildren ? "visible" : "hidden" }}
          onClick={() => toggleExpand(node.id)}>
          {isExpanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
        </IconButton>

        <DragIndicatorIcon fontSize="small" sx={{ color: "text.disabled", mr: 0.5 }} />

        <Typography sx={{ flex: 1, fontSize: 14, color: node.is_active ? "text.primary" : "text.disabled" }}>
          {node.name}
          {!node.is_active && <Chip label="inactive" size="small" sx={{ ml: 1, height: 18, fontSize: 11 }} />}
        </Typography>

        <Tooltip title="Add child">
          <IconButton size="small" onClick={() => onAdd(node.id)}>
            <AddIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Edit">
          <IconButton size="small" onClick={() => onEdit(node)}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete (and all children)">
          <IconButton size="small" color="error" onClick={() => onDelete(node)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {hasChildren && (
        <Collapse in={isExpanded}>
          {node.children.map(child => (
            <CategoryNode key={child.id} node={child} depth={depth + 1}
              onAdd={onAdd} onEdit={onEdit} onDelete={onDelete}
              expandedIds={expandedIds} toggleExpand={toggleExpand} />
          ))}
        </Collapse>
      )}
    </Box>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function ARCCustomFormBuilder() {
  const [innerTab, setInnerTab] = useState(0);
  const [formType, setFormType] = useState("original"); // "original" | "custom"
  const [configLoading, setConfigLoading] = useState(true);

  const [flat, setFlat] = useState([]);
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedIds, setExpandedIds] = useState(new Set());

  // dialog state
  const [dialog, setDialog] = useState({ open: false, mode: "add", node: null, parentId: null });
  const [form, setForm] = useState({ name: "", sort_order: 0, is_active: true });
  const [saving, setSaving] = useState(false);

  // delete confirm
  const [deleteDialog, setDeleteDialog] = useState({ open: false, node: null });

  const [snack, setSnack] = useState({ open: false, msg: "", severity: "success" });

  // ── Load form type config ─────────────────────────────────────────────────
  useEffect(() => {
    api.get("/ar-form-config")
      .then(r => { if (r.data?.form_type) setFormType(r.data.form_type); })
      .catch(() => {})
      .finally(() => setConfigLoading(false));
  }, []);

  const saveFormType = async (newType) => {
    setFormType(newType);
    try {
      const current = await api.get("/ar-form-config");
      await api.put("/ar-form-config", { ...current.data, form_type: newType });
      setSnack({ open: true, msg: `Form type set to "${newType}"`, severity: "success" });
    } catch {
      setSnack({ open: true, msg: "Failed to save form type", severity: "error" });
    }
  };

  // ── Load categories ───────────────────────────────────────────────────────
  const loadCategories = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/arc-custom-categories");
      setFlat(res.data || []);
      setTree(buildTree(res.data || []));
    } catch {
      setSnack({ open: true, msg: "Failed to load categories", severity: "error" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (innerTab === 1) loadCategories(); }, [innerTab, loadCategories]);

  // ── Expand / collapse ─────────────────────────────────────────────────────
  const toggleExpand = (id) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const expandAll = () => setExpandedIds(new Set(flat.map(n => n.id)));
  const collapseAll = () => setExpandedIds(new Set());

  // ── Dialog helpers ────────────────────────────────────────────────────────
  const openAdd = (parentId = null) => {
    setForm({ name: "", sort_order: 0, is_active: true });
    setDialog({ open: true, mode: "add", node: null, parentId });
  };

  const openEdit = (node) => {
    setForm({ name: node.name, sort_order: node.sort_order, is_active: !!node.is_active });
    setDialog({ open: true, mode: "edit", node, parentId: node.parent_id });
  };

  const closeDialog = () => setDialog(d => ({ ...d, open: false }));

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      if (dialog.mode === "add") {
        await api.post("/arc-custom-categories", {
          parent_id: dialog.parentId,
          name: form.name.trim(),
          sort_order: Number(form.sort_order) || 0,
        });
      } else {
        await api.put(`/arc-custom-categories/${dialog.node.id}`, {
          name: form.name.trim(),
          sort_order: Number(form.sort_order) || 0,
          is_active: form.is_active,
        });
      }
      setSnack({ open: true, msg: dialog.mode === "add" ? "Category added" : "Category updated", severity: "success" });
      closeDialog();
      loadCategories();
    } catch {
      setSnack({ open: true, msg: "Save failed", severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/arc-custom-categories/${deleteDialog.node.id}`);
      setSnack({ open: true, msg: "Deleted", severity: "success" });
      setDeleteDialog({ open: false, node: null });
      loadCategories();
    } catch {
      setSnack({ open: true, msg: "Delete failed", severity: "error" });
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ mt: 2 }}>
      <Tabs value={innerTab} onChange={(_, v) => setInnerTab(v)}
        sx={{
          mb: 2,
          "& .MuiTab-root": { textTransform: "none", fontWeight: 600, minWidth: 140 },
          "& .MuiTabs-indicator": { height: 3, borderRadius: 2 },
        }}
      >
        <Tab label="Form Type" />
        <Tab label="Category Tree" />
      </Tabs>

      {/* ── Tab 0: Form Type toggle ─────────────────────────────────────── */}
      {innerTab === 0 && (
        <Paper sx={{ p: 3, borderRadius: 3, border: "1px solid rgba(47,99,173,0.12)", boxShadow: "none" }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            Form Type
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Choose which form members see when submitting a new architectural request.
          </Typography>

          {configLoading ? <CircularProgress size={24} /> : (
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              {["original", "custom"].map(type => (
                <Paper
                  key={type}
                  onClick={() => saveFormType(type)}
                  elevation={0}
                  sx={{
                    flex: 1, minWidth: 220, p: 2.5, borderRadius: 2, cursor: "pointer",
                    border: "2px solid",
                    borderColor: formType === type ? "primary.main" : "rgba(0,0,0,0.12)",
                    bgcolor: formType === type ? "rgba(47,99,173,0.06)" : "background.paper",
                    transition: "all 0.15s",
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, textTransform: "capitalize", mb: 0.5 }}>
                    {type} Form
                    {formType === type && (
                      <Chip label="Active" size="small" color="primary" sx={{ ml: 1, height: 20, fontSize: 11 }} />
                    )}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {type === "original"
                      ? "Standard form with Request Type and Subtype dropdowns."
                      : "Custom cascading category form — up to 5 levels deep."}
                  </Typography>
                </Paper>
              ))}
            </Box>
          )}

          {formType === "custom" && (
            <Alert severity="info" sx={{ mt: 3, borderRadius: 2 }}>
              Members will now see the <strong>Custom Form</strong> with cascading category dropdowns.
              Manage your category hierarchy in the <strong>Category Tree</strong> tab.
            </Alert>
          )}
        </Paper>
      )}

      {/* ── Tab 1: Category tree ─────────────────────────────────────────── */}
      {innerTab === 1 && (
        <Paper sx={{ p: 3, borderRadius: 3, border: "1px solid rgba(47,99,173,0.12)", boxShadow: "none" }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2, flexWrap: "wrap", gap: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Category Hierarchy
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button size="small" variant="text" onClick={expandAll}>Expand All</Button>
              <Button size="small" variant="text" onClick={collapseAll}>Collapse All</Button>
              <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => openAdd(null)}>
                Add Root Category
              </Button>
            </Box>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Build a hierarchy up to 5 levels deep. Click the <strong>+</strong> icon on any node to add a child.
          </Typography>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress /></Box>
          ) : tree.length === 0 ? (
            <Box sx={{ py: 4, textAlign: "center", color: "text.secondary" }}>
              <Typography>No categories yet.</Typography>
              <Button sx={{ mt: 1 }} variant="outlined" startIcon={<AddIcon />} onClick={() => openAdd(null)}>
                Add your first category
              </Button>
            </Box>
          ) : (
            <Box sx={{ border: "1px solid rgba(47,99,173,0.10)", borderRadius: 2, p: 1 }}>
              {tree.map(node => (
                <CategoryNode key={node.id} node={node} depth={0}
                  onAdd={openAdd} onEdit={openEdit}
                  onDelete={n => setDeleteDialog({ open: true, node: n })}
                  expandedIds={expandedIds} toggleExpand={toggleExpand} />
              ))}
            </Box>
          )}
        </Paper>
      )}

      {/* ── Add / Edit dialog ─────────────────────────────────────────────── */}
      <Dialog open={dialog.open} onClose={closeDialog} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {dialog.mode === "add" ? (dialog.parentId ? "Add Child Category" : "Add Root Category") : "Edit Category"}
        </DialogTitle>
        <DialogContent sx={{ pt: "12px !important", display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            label="Name" fullWidth autoFocus
            value={form.name}
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            onKeyDown={e => e.key === "Enter" && handleSave()}
          />
          <TextField
            label="Sort Order" type="number" fullWidth
            value={form.sort_order}
            onChange={e => setForm(f => ({ ...f, sort_order: e.target.value }))}
          />
          {dialog.mode === "edit" && (
            <FormControlLabel
              control={<Switch checked={!!form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} />}
              label="Active"
            />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || !form.name.trim()}>
            {saving ? <CircularProgress size={18} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete confirm ────────────────────────────────────────────────── */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, node: null })} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: "error.main" }}>Delete Category?</DialogTitle>
        <DialogContent>
          <Typography>
            Delete <strong>{deleteDialog.node?.name}</strong> and all its sub-categories?
            This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteDialog({ open: false, node: null })}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack(s => ({ ...s, open: false }))}>
        <Alert severity={snack.severity} sx={{ width: "100%" }}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
